import { axiosInstanceV2, axiosInstance } from "../axios";
import { IUser, IUserRegistrationData } from "../types";
import { snakeCaseDictToCamelCaseDict, cleanCPFCNPJ } from "../utils";
import { masks } from "../../utils/masks";

export interface GetTokenResponse {
  token: string;
  tokenExpirationDate?: string;
  user: IUser;
}

export async function getToken({
  username,
  password,
  agreement_id,
  integratedLogin,
  idp,
}: {
  username: string;
  password?: string;
  integratedLogin?: string;
  idp?: string;
  agreement_id?: number | string;
}): Promise<GetTokenResponse> {
  const loginEndpoints: Record<string, string> = {
    neo: '/integrations/neo/login/',
    voalle: '/voalle/login/',
    surf: '/surf/login/',
    medlar: '/medlar/login/',
    mk: '/mk/login/',
    maxx: '/maxx/login/',
    youcast: '/youcast/login/',
    isuper: '/isuper/login/',
    leste: '/leste/login/',
    sercomtel: '/sercomtel/login/',
  };

  if (integratedLogin && typeof loginEndpoints[integratedLogin] === 'string') {
    const payload =
      integratedLogin === 'surf' || integratedLogin === 'medlar'
        ? { username: username }
        : integratedLogin === 'sercomtel'
          ? { document: username, password }
          : { username, password, agreement_id, idp: idp || integratedLogin };

    if (integratedLogin === 'neo') {
      return axiosInstanceV2.post(loginEndpoints[integratedLogin], payload).then(({ data: { token, ...data } }) => ({
        token,
        tokenExpirationDate: data.token_expiration_date,
        user: snakeCaseDictToCamelCaseDict(data) as IUser,
      }));
    }

    return axiosInstance.post(loginEndpoints[integratedLogin], payload).then(({ data: { token, ...data } }) => ({
      token,
      tokenExpirationDate: data.token_expiration_date,
      user: snakeCaseDictToCamelCaseDict(data) as IUser,
    }));
  }

  return axiosInstance
    .post('/users/tokens/' + (agreement_id ? `?agreement_id=${agreement_id}` : ''), {
      username,
      password,
      agreement_id,
    })
    .then(({ data }) => ({
      token: data.token,
      user: snakeCaseDictToCamelCaseDict(data.user) as IUser,
    }));
}

export async function getExClientLogin({ cpf }: { cpf: string }): Promise<GetTokenResponse> {
  return axiosInstanceV2.post('/agreement/ex_client/', { cpf }).then(({ data }) => ({
    token: data.token,
    user: snakeCaseDictToCamelCaseDict(data.user) as IUser,
  }));
}

export async function forgotPass({ email }: { email: string }) {
  return axiosInstance.post('/users/reset-password/', { email });
}

const REGISTER_TIMEOUT_MS = 20000;

export function registerUser(user: IUserRegistrationData, agreementId: number): Promise<IUser & { token: string }> {
  return axiosInstance
    .post(
      `/profile/?agreement_id=${agreementId}`,
      { ...user, cpf: cleanCPFCNPJ(user.cpf) },
      { timeout: REGISTER_TIMEOUT_MS }
    )
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IUser & { token: string });
}

export async function validateCPF(cnpjCpf: string, agreementId: number) {
  return axiosInstanceV2
    .post(`/agreement/validate/?agreement_id=${agreementId}`, {
      cpf: cleanCPFCNPJ(cnpjCpf),
    })
    .then(({ data }) => data);
}

export async function vinculateCPF(cnpjCpf: string, agreementId: number, productId?: number) {
  const queryParams = new URLSearchParams({ agreement_id: agreementId.toString() });
  if (productId) {
    queryParams.append('product_id', productId.toString());
  }
  return axiosInstanceV2
    .post(`/agreement/vinculate/?${queryParams.toString()}`, {
      cpf: cleanCPFCNPJ(cnpjCpf),
    })
    .then(({ data }) => data);
}

export function validatePromocode(code: string) {
  return axiosInstance
    .post('/validate_promocode/', { promocode: code })
    .then(({ data }) => data);
}

export function vinculateExternalCode(code: string, agreementId: number, productId?: number) {
  const queryParams = new URLSearchParams({ agreement_id: agreementId.toString() });
  if (productId) {
    queryParams.append('product_id', productId.toString());
  }
  return axiosInstanceV2
    .post(`/agreement/vinculate/?${queryParams.toString()}`, {
      external_code: masks.unmask(code),
    })
    .then(({ data }) => data);
}
